import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const StoryHero = lazy(() => import('../sections/StoryHero'));
const StoryProblem = lazy(() => import('../sections/StoryProblem'));
const StorySolution = lazy(() => import('../sections/StorySolution'));
const StoryFeatures = lazy(() => import('../sections/StoryFeatures'));
const StoryCTA = lazy(() => import('../sections/StoryCTA'));

const sections = [
  { id: 'hero',     component: StoryHero     },
  { id: 'problem',  component: StoryProblem  },
  { id: 'solution', component: StorySolution },
  { id: 'features', component: StoryFeatures },
  { id: 'cta',      component: StoryCTA      },
];

const HomeLayout = () => {
  const location = useLocation();
  const [current, setCurrent] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrent(0);
    
    const scrollContainer = document.querySelector('.scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
    }
    
    if (refs.current[0]) {
      setTimeout(() => {
        const firstSection = refs.current[0];
        if (firstSection && scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
        }
      }, 50);
    }
  }, [location.pathname]);

  useEffect(() => {
    const io = new IntersectionObserver(
      entries => {
        const most = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (most) setCurrent(Number(most.target.dataset.idx));
      },
      { threshold: Array.from({ length: 10 }, (_, i) => (i + 1) / 10) }
    );
    const currentRefs = refs.current;
    currentRefs.forEach(el => el && io.observe(el));
    return () => currentRefs.forEach(el => el && io.unobserve(el));
  }, []);

  const onKey = e => {
    if (e.key === 'ArrowDown' && current < sections.length - 1) {
      refs.current[current + 1]?.scrollIntoView({ behavior: 'smooth' });
    }
    if (e.key === 'ArrowUp' && current > 0) {
      refs.current[current - 1]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="block md:hidden bg-black text-gray-300">
        <Navbar />
        <Suspense fallback={<div>Loading...</div>}>
          {sections.map(S => (
            <S.component key={S.id} isMobile />
          ))}
        </Suspense>
        <Footer compact />
      </div>

      <div
        className="hidden md:flex flex-col h-screen"
        tabIndex={0}
        onKeyDown={onKey}
      >
        <header >
          <Navbar currentSection={current} totalSections={sections.length} />
        </header>

        <div
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scroll-container"
        >
          <Suspense fallback={<div>Loading...</div>}>
            {sections.map((S, i) => (
              <motion.div
                key={S.id}
                ref={el => (refs.current[i] = el)}
                data-idx={i}
                className="h-full min-h-full w-full flex-shrink-0 snap-start bg-black"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.7 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <S.component isMobile={false} />
              </motion.div>
            ))}
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default HomeLayout;
